/** Returns the `decimal` number separator based on locale */
function getDecimalSeparator(locale?: string) {
    const testNumber = 1.9;
    const testText = testNumber.toLocaleString(locale);
    const one = (1).toLocaleString(locale);
    const nine = (9).toLocaleString(locale);
    const pattern = `${one}(.+)${nine}`;

    const result = new RegExp(pattern).exec(testText);

    return result?.[1] || ".";
}

/**
 * A regex that matches a string of length 1 (i.e. a standalone character)
 * if and only if it is a floating-point number character as defined by W3C:
 * https://www.w3.org/TR/2012/WD-html-markup-20120329/datatypes.html#common.data.float
 */
function isFloatingPointNumericCharacter(character: string, locale: string | undefined) {
    const decimalSeparator = getDecimalSeparator(locale).replace(".", "\\.");
    const localeFloatingPointNumericCharacterRegex = new RegExp("^[0-9\\+\\-" + decimalSeparator + "]$");
    return localeFloatingPointNumericCharacterRegex.test(character);
}

export function isValidNumericKeyboardEvent(e: KeyboardEvent, locale: string | undefined) {
    // unit tests may not include e.key. don't bother disabling those events.
    if (e.key == null) {
        return true;
    }

    // allow modified key strokes that may involve letters and other
    // non-numeric/invalid characters (Cmd + A, Cmd + C, Cmd + V, Cmd + X).
    if (e.ctrlKey || e.altKey || e.metaKey) {
        return true;
    }

    // keys that print a single character when pressed have a `key` name of
    // length 1. every other key has a longer `key` name (e.g. "Backspace",
    // "ArrowUp", "Shift"). since none of those keys can print a character
    // to the field--and since they may have important native behaviors
    // beyond printing a character--we don't want to disable their effects.
    const isSingleCharKey = e.key.length === 1;
    if (!isSingleCharKey) {
        return true;
    }

    // now we can simply check that the single character that wants to be printed
    // is a floating-point number character that we're allowed to print.
    return isFloatingPointNumericCharacter(e.key, locale);
}

export function toLocaleString(num: number, locale?: string) {
    return sanitizeNumericInput(num.toLocaleString(locale), locale);
}

export function toMaxPrecision(value: number, maxPrecision: number) {
    const scaleFactor = Math.pow(10, maxPrecision);
    return Math.round(value * scaleFactor) / scaleFactor;
}

export function parseStringToStringNumber(value: string, locale?: string) {
    if (parseFloat(value).toString() === value.toString()) {
        return value.toString();
    }

    const decimalSeparator = getDecimalSeparator(locale);
    const sanitizedString = sanitizeNumericInput(value, locale);

    return sanitizedString.replace(decimalSeparator, ".");
}

export function sanitizeNumericInput(value: string, locale?: string) {
    return value
        .split("")
        .filter((c) => isFloatingPointNumericCharacter(c, locale))
        .join("");
}

export function clamp(value: number, min?: number, max?: number) {
    value = min != null && value < min ? min : value;
    return max != null && value > max ? max : value;
}

export function isValidNumber(value: unknown) {
    return value !== null && typeof value !== "undefined" && !Number.isNaN(Number(value));
}
